import { React, useEffect, useState } from "react";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { Button, Tabs, Tab } from "@mui/material";
import { api } from "../../utils/api";
import SelfSignUpCard from "./SelfSignUpCard";
import SelfSignupModal from "../../components/SelfSignUpModal";
import CreateSignUpModal from "../../components/CreateSignUpModal"; // NEW
import { applyDealerPrices } from "../../utils/signupLinkPrice";
import { canManageSignupLinks, resolvePartnerCompanyId } from "../../utils/signupLinkAccess";

const SelfSignUp = ({ selectedCompany }) => {
  const [productOptions, setProductOptions] = useState([]);
  const [partnerProducts, setPartnerProducts] = useState([]);
  const [partnerUsers, setPartnerUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [activeTab, setActiveTab] = useState("Active"); // Tab state

  useEffect(() => {
    fetchProducts();
    fetchPartnerProducts();
    fetchAllPartnerUsers();
  }, [sessionStorage.getItem("currentUser")]);

  useEffect(() => {
    if (selectedProduct) {
      setModalOpen(true);
    }
  }, [selectedProduct]);

  const fetchProducts = async () => {
    const selectedPartnerCompany = resolvePartnerCompanyId(selectedCompany);
    console.log("fetchProducts called, selectedPartnerCompany:", selectedPartnerCompany);
    try {
      const response = await api(`v1/selfsignup/${selectedPartnerCompany}`, "GET", null);
      const signUpLinks = response.data.signupLinks;
      console.log("Fetched signup links:", signUpLinks.length, "links");
      setProductOptions(signUpLinks);
    } catch (error) {
      console.error("Error loading Products:", error);
    }
    setLoading(false);
  };

  const fetchPartnerProducts = async () => {
    const selectedPartnerCompany = resolvePartnerCompanyId(selectedCompany);
    try {
      const [pricingResponse, dealerResponse] = await Promise.all([
        api(`v1/pricing/company/${selectedPartnerCompany}?getRRP=true`, "GET", null),
        api(`v1/partner/${selectedPartnerCompany}/pricing`, "GET", null).catch((error) => {
          console.error("Error loading dealer pricing:", error);
          return null;
        })
      ]);
      if (pricingResponse.status === 200) {
        const productPricings = pricingResponse.data.data.allProducts || [];
        const pricingOptions = dealerResponse?.data?.success
          ? dealerResponse.data.data.pricingOptions || []
          : [];
        setPartnerProducts(applyDealerPrices(productPricings, pricingOptions));
      }
    } catch (error) {
      console.error("Error loading partner products:", error);
    }
  };

  const fetchAllPartnerUsers = async () => {
    const selectedPartnerCompany = resolvePartnerCompanyId(selectedCompany);
    try {
      const response = await api("v1/partner/company/" + selectedPartnerCompany + "/user", "GET", null);
      if (response.data.success) {
        console.log("partner users: ", response.data);
        setPartnerUsers(response.data.data.partnerUsers);
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedProduct(null);
    setCreateModalOpen(false);
    setSelectedLink(null);
  };

  const handleEditLink = (product) => {
    setSelectedLink(product);
    setModalOpen(false);
    setCreateModalOpen(true);
  };

  // Filter links based on selected tab
  const filteredProducts = productOptions.filter((product) => product.status === activeTab);

  return (
    <div className="page product flex flex-column items-center p-4 w-full">
      {loading && <GlobalLoadingSpinner />}

      {canManageSignupLinks() && (
        <div className="pageTitle w-full">
          <Button className="mb-5" variant="contained" color="primary" onClick={() => setCreateModalOpen(true)}>
            Create Sign Up Link
          </Button>
        </div>
      )}

      {/* Tabs for Active & Disabled Links */}
      <Tabs value={activeTab} onChange={(event, newValue) => setActiveTab(newValue)} centered>
        <Tab label="Active Links" value="Active" />
        <Tab label="Disabled Links" value="Disabled" />
      </Tabs>

      <div className="flex w-full mt-4">
        <div className="page-content min-h-h-screen flex flex-column justify-start w-full">
          <div className="flex flex-row flex-wrap items-end gap-4 items-center">
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <SelfSignUpCard
                  key={product._id + index}
                  product={product}
                  partnerProducts={partnerProducts}
                  handleClick={() => handleProductClick(product)}
                />
              ))
            ) : (
              <p className="text-center text-gray-500 w-full">No {activeTab.toLowerCase()} sign-up links found.</p>
            )}
          </div>
        </div>
      </div>

      {/* View Sign Up Link Modal */}
      <SelfSignupModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        product={selectedProduct}
        partnerProducts={partnerProducts}
        handleEditLink={handleEditLink}
        onStatusUpdated={() => fetchProducts()}
      />

      {/* Create / Edit Payment Link Modal — always mount so Edit works even if
          partner pricing is missing (modal falls back to the link's product). */}
      <CreateSignUpModal
        isOpen={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setSelectedLink(null);
          setSelectedProduct(null);
        }}
        partnerProducts={partnerProducts}
        partnerUsers={partnerUsers}
        onSignUpCreated={fetchProducts}
        existingLink={selectedLink}
        selectedCompany={resolvePartnerCompanyId(selectedCompany)}
      />
    </div>
  );
};

export default SelfSignUp;
